import { useState, useEffect, lazy, Suspense } from 'react';
import { useSiteSettings } from './context/SiteSettingsContext';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, LazyMotion, domAnimation } from 'framer-motion';
import { ReactLenis } from 'lenis/react';
import { HelmetProvider } from 'react-helmet-async';
import 'lenis/dist/lenis.css';

// --- Critical above-the-fold: synchronous imports ---
import CustomCursor from './components/CustomCursor';
import Preloader from './components/Preloader';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import SEO from './components/SEO';


// --- Below-the-fold: lazy imports for code splitting ---
const SelectedWork = lazy(() => import('./components/SelectedWork'));
const AnimatedDivider = lazy(() => import('./components/AnimatedDivider'));
const Services = lazy(() => import('./components/Services'));
const BeforeAfter = lazy(() => import('./components/BeforeAfter'));
const Footer = lazy(() => import('./components/Footer'));
const ConsultationModal = lazy(() => import('./components/ConsultationModal'));
const InteriorsPage = lazy(() => import('./pages/InteriorsPage'));
const GraphicDesignPage = lazy(() => import('./pages/GraphicDesignPage'));
const ProductDesignPage = lazy(() => import('./pages/ProductDesignPage'));
const BookNow = lazy(() => import('./pages/BookNow'));

// Lightweight placeholder shown while lazy chunks are downloading
const SectionFallback = () => <div className="min-h-[50vh] bg-transparent" />;

// Premium fallback loader for full pages
const PageFallbackLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#f4ceb3] text-[#9c1b42]">
    <div className="w-8 h-8 border-t-2 border-r-2 border-[#9c1b42] animate-spin rounded-full mb-4" />
    <span className="text-[10px] font-sans tracking-widest uppercase opacity-75">
      Loading Archive // Pomie
    </span>
  </div>
);
// ScrollToTop resets scroll position on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
export default function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Only show preloader on the very first visit of the session
  const [isLoading, setIsLoading] = useState(
    () => !sessionStorage.getItem('pomie_visited')
  );
  const { settings } = useSiteSettings();

  const handlePreloaderComplete = () => {
    sessionStorage.setItem('pomie_visited', '1');
    setIsLoading(false);
  };

  return (
    <LazyMotion features={domAnimation}>
      <ScrollToTop />
      <Routes>
        <Route
          path="/book-now"
          element={
            <Suspense fallback={<PageFallbackLoader />}>
              <BookNow />
            </Suspense>
          }
        />
        {/* Category pages */}
        <Route
          path="/interiors"
          element={
            <Suspense fallback={<PageFallbackLoader />}>
              <InteriorsPage />
            </Suspense>
          }
        />
        <Route
          path="/graphic-design"
          element={
            <Suspense fallback={<PageFallbackLoader />}>
              <GraphicDesignPage />
            </Suspense>
          }
        />
        <Route
          path="/product-design"
          element={
            <Suspense fallback={<PageFallbackLoader />}>
              <ProductDesignPage />
            </Suspense>
          }
        />

        {/* / — Main homepage */}
        <Route
          path="/"
          element={
            <HelmetProvider>
              <SEO title={settings?.title} description={settings?.seoDescription} />

              {/* Preloader - shown only on initial session visit */}
              {isLoading && <Preloader onComplete={handlePreloaderComplete} />}

              {/* Main Site Content */}
              <AnimatePresence>
                {!isLoading && (
                  <ReactLenis root options={{ lerp: 0.1, duration: 1.5, smoothWheel: true }}>
                    <motion.div
                      className="bg-[#f4ceb3] min-h-screen text-[#9c1b42] selection:bg-[#9c1b42] selection:text-[#f4ceb3] relative"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <CustomCursor />
                      <Navbar onBookClick={() => setIsModalOpen(true)} />
                      <main>
                        {/* Hero loads synchronously — it IS the LCP element */}
                        <Hero />

                        {/* All below-the-fold sections share one Suspense boundary */}
                        <Suspense fallback={<SectionFallback />}>
                          <AnimatedDivider className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" />
                          <SelectedWork />
                          <AnimatedDivider className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" />
                          <BeforeAfter />
                          <AnimatedDivider className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" />
                          <Services />
                        </Suspense>
                      </main>

                      <Suspense fallback={null}>
                        <Footer onBookClick={() => setIsModalOpen(true)} />
                        <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
                      </Suspense>
                    </motion.div>
                  </ReactLenis>
                )}
              </AnimatePresence>
            </HelmetProvider>
          }
        />
      </Routes>
    </LazyMotion>
  );
}
